import { cancelDebounce, debounce } from "@/utils/bouncer";
import { createLogger } from "@paparazzi/utils/debug";
import api from "@virgodev/bazaar/functions/api";
import { useLocalStorageStore } from "@virgodev/bazaar/functions/localstorage/store";
import { defineStore } from "pinia";
import sortBy from "sort-by";
import { computed, ref, watch, type Ref } from "vue";
import { useCartStore } from "./cart";
import type { Shipper } from "./defs/shop_defs";
import { usePromosStore } from "./promos";
import { useShopStore } from "./shop";

const log = createLogger("shipping");

export const useShippingStore = defineStore("shipping", () => {
  let abortController: AbortController | undefined;
  const cart = useCartStore();
  const shop = useShopStore();
  const promos = usePromosStore();
  const storage = useLocalStorageStore();

  const choices: Ref<Shipper[]> = ref([]);
  const shipper = computed<Shipper | undefined>(() => {
    return choices.value.find(
      (c) => `${c.ShipMethodID}` == `${cart.object.shipping_choice}`,
    );
  });
  const errors: Ref<string[]> = ref([]);
  const attempts: Ref<number> = ref(0);
  const favorite = ref<string>(storage.get("preferred-shipper", ""));
  const status = ref<"loading" | "requesting" | "syncing" | "">("loading");

  watch([() => cart.object.shipping_choice, choices], () => {
    log("choices changed");
    if (cart.object.shipping_choice) {
      const existing = choices.value.find(
        (c: Shipper) =>
          `${c.ShipMethodID}` === `${cart.object.shipping_choice}`,
      );
      if (!existing) {
        log(
          "shipping choice matches no options",
          choices.value.map((c) => c.ShipMethodID),
          "does not contain:",
          `${cart.object.shipping_choice}`,
        );
        cart.object.shipping_choice = undefined;
      }
    }

    if (!cart.object.shipping_choice) {
      let chosen: Shipper | undefined = undefined;

      if (!chosen && favorite.value) {
        chosen = choices.value.find(
          (c: Shipper) => `${c.ShipMethodID}` === `${favorite.value}`,
        );
        if (chosen) {
          log("favorite shipper selected", favorite.value);
        }
      }

      if (!chosen) {
        // pick the cheapest
        let cheapest: Shipper[] = choices.value.slice();

        // will call can't be auto-selected
        const index = cheapest.findIndex((c) => {
          return c.ShipMethodID === "1012" || /will.?call/i.test(c.Description);
        });
        if (index > -1) {
          cheapest.splice(index, 1);
        }

        chosen = cheapest.reduce(
          (a: Shipper | undefined, b: Shipper) =>
            a === undefined || b.Price < a.Price ? b : a,
          undefined,
        );
      }
      if (chosen) {
        cart.object.shipping_choice = parseInt(chosen.ShipMethodID);
        cart.object.changed = new Date().toISOString();
      }
    }
  });
  watch(shipper, () => {
    if (shipper.value) {
      cart.object.shipping_price = shipper.value.Price;
      cart.object.shipping_description = shipper.value.Description;
    } else {
      cart.object.shipping_price = null;
      cart.object.shipping_description = null;
    }
  });
  watch(favorite, () => {
    storage.put("preferred-shipper", favorite.value);
  });

  async function updateShipping() {
    if (cart.object.shipping_address?.id && cart.object.items.length > 0) {
      errors.value = [];
      if (await debounce("update-shipping")) {
        log("updating shipping now");
        status.value = "loading";
        let response = null;
        try {
          if (cart.items.length === 0) {
            errors.value = ["Please choose items and try again"];
          } else if (!cart.object.shipping_address) {
            errors.value = ["Please choose a shipping address"];
          } else {
            status.value = "requesting";
            response = await getOptions();
          }
        } catch (ex) {
          console.warn("exception", ex);
          errors.value = ["Failed to get shipping options"];
        }

        attempts.value += 1;

        if (response) {
          if (response.errors.length > 0) {
            errors.value = response.errors;
          } else {
            choices.value = [];

            // prevent duplicate shipping methods
            for (let choice of response.options) {
              if (
                !choices.value.some(
                  (c) => c.ShipMethodID == choice.ShipMethodID,
                )
              ) {
                choices.value.push(choice);
              }
            }
            log("shipping choices loaded");
          }
        }
        status.value = "";
      }
    } else {
      cancelDebounce("update-shipping");
    }
  }

  async function getOptions(): Promise<any | null> {
    await shop.ready;
    await promos.applyPromotions();

    if (errors.value.length > 0 && !cart.object.id) {
      await cart.getCartId();
    }
    const items: string = cart.items
      .slice(0, 50)
      .map((i) => i.product?.id)
      .filter((i) => i)
      .join(",");
    const params: { [key: string]: string } = {
      cart: `${cart.object.id || ""}`,
      address: cart.object.shipping_address
        ? `${cart.object.shipping_address.id}`
        : "",
      subtotal: `${cart.subtotal}`,
      items,
      inventory_source: cart.inventorySource,
      promos: cart.object.shippingPromos || "",
      debug: "true",
    };
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const opts = await api({
      url: "cart/shipping/",
      params,
      method: "GET",
      options: { timeout: 500, signal: abortController.signal },
    });
    abortController = undefined;
    if (opts.ok && opts.body.options) {
      opts.body.options.forEach((o: Shipper) => {
        o.Price = parseFloat(o.Price.toString());
      });
      opts.body.options.sort(sortBy("Price"));
    }
    return opts.ok ? opts.body : null;
  }

  return {
    status,
    errors,
    choices,
    shipper,
    favorite,
    updateShipping,
  };
});
